import { useCallback } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/shared/ui/tooltip'
import { ColorPaletteSVGIcon } from '@/shared/assets'
import { ColorPickerPopup } from './ColorPickerPopup'
import { AlignmentButton } from './AlignmentButton'
import { FontSelectorButton } from './FontSelectorButton'
import { BackgroundToggleButton } from './BackgroundToggleButton'
import { BackgroundColorButton } from './BackgroundColorButton'
import { extractAlphaFromHex } from '../utils'
import type { TextAlignment, FontFamily } from '../types'
import classes from './MarkingEditorToolbar.module.scss'

/**
 * Пропсы для компонента MarkingEditorToolbar
 */
export interface MarkingEditorToolbarProps {
    /**
     * Текущий цвет текста в формате hex с alpha (например, "#056FFF80")
     */
    textColor?: string
    /**
     * Обработчик изменения цвета текста
     */
    onTextColorChange?: (color: string) => void
    /**
     * Текущее выравнивание текста
     */
    alignment?: TextAlignment
    /**
     * Обработчик изменения выравнивания
     */
    onAlignmentChange?: (alignment: TextAlignment) => void
    /**
     * Текущий шрифт текста
     */
    fontFamily?: FontFamily
    /**
     * Обработчик изменения шрифта
     */
    onFontFamilyChange?: (font: FontFamily) => void
    /**
     * Текущее состояние подложки (включена/выключена)
     */
    hasBackground?: boolean
    /**
     * Обработчик изменения состояния подложки
     */
    onHasBackgroundChange?: (value: boolean) => void
    /**
     * Текущий цвет подложки в формате hex с alpha (например, "#056FFF80")
     */
    backgroundColor?: string | null
    /**
     * Обработчик изменения цвета подложки
     */
    onBackgroundColorChange?: (color: string) => void
    /**
     * Сохраненные кастомные цвета для текста (максимум 4)
     */
    customTextColors?: string[]
    /**
     * Обработчик изменения сохраненных кастомных цветов для текста
     */
    onCustomTextColorsChange?: (colors: string[]) => void
    /**
     * Сохраненные кастомные цвета для подложки (максимум 4)
     */
    customBackgroundColors?: string[]
    /**
     * Обработчик изменения сохраненных кастомных цветов для подложки
     */
    onCustomBackgroundColorsChange?: (colors: string[]) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
}

/**
 * Компонент панели инструментов редактора маркировки
 * Содержит кнопки: палитра, выравнивание, шрифт, подложка, цвет подложки
 */
export function MarkingEditorToolbar({
    textColor = '#000000FF',
    onTextColorChange,
    alignment,
    onAlignmentChange,
    fontFamily,
    onFontFamilyChange,
    hasBackground,
    onHasBackgroundChange,
    backgroundColor,
    onBackgroundColorChange,
    customTextColors = [],
    onCustomTextColorsChange,
    customBackgroundColors = [],
    onCustomBackgroundColorsChange,
    className,
}: MarkingEditorToolbarProps) {
    /**
     * Извлекаем hex цвет для отображения (без alpha)
     */
    const displayColor = extractAlphaFromHex(textColor).hex

    /**
     * Обработчик переключения подложки
     * При включении подложки устанавливает дефолтный цвет, если его нет
     */
    const handleBackgroundToggle = useCallback(
        (value: boolean) => {
            if (onHasBackgroundChange) {
                onHasBackgroundChange(value)
                // Если включаем подложку и цвет не задан, устанавливаем дефолтный белый с прозрачностью
                if (value && !backgroundColor && onBackgroundColorChange) {
                    onBackgroundColorChange('#FFFFFF80') // Белый с 50% прозрачностью
                }
            }
        },
        [onHasBackgroundChange, backgroundColor, onBackgroundColorChange]
    )

    return (
        <TooltipProvider>
            <div className={cn(classes.toolbar, className)}>
                {/* Кнопка палитры (20x20, круг) */}
                <div className={classes.buttonWrapper}>
                    {onTextColorChange ? (
                        <ColorPickerPopup
                            value={textColor}
                            onChange={onTextColorChange}
                            customColors={customTextColors}
                            onCustomColorsChange={onCustomTextColorsChange}
                        >
                            <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className={classes.colorPickerButton}
                                        aria-label="Выбрать цвет текста"
                                    >
                                        <ColorPaletteSVGIcon
                                            className={classes.colorPickerIcon}
                                            style={{
                                                color: displayColor,
                                            }}
                                        />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    sideOffset={4}
                                    className="z-[60]"
                                >
                                    Цвет текста
                                </TooltipContent>
                            </Tooltip>
                        </ColorPickerPopup>
                    ) : (
                        <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className={classes.colorPickerButton}
                                    aria-label="Выбрать цвет текста"
                                    disabled
                                >
                                    <ColorPaletteSVGIcon
                                        className={classes.colorPickerIcon}
                                        style={{
                                            color: displayColor,
                                        }}
                                    />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                sideOffset={4}
                                className="z-[60]"
                            >
                                Цвет текста
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>

            {/* Кнопка выравнивания (15x12) */}
            <div className={classes.buttonWrapper}>
                {alignment !== undefined && onAlignmentChange ? (
                    <AlignmentButton
                        value={alignment}
                        onChange={onAlignmentChange}
                    />
                ) : (
                    <AlignmentButton
                        value="center"
                        disabled
                    />
                )}
            </div>

            {/* Кнопка выбора шрифта (21x18) */}
            <div className={classes.buttonWrapper}>
                {fontFamily !== undefined && onFontFamilyChange ? (
                    <FontSelectorButton
                        value={fontFamily}
                        onChange={onFontFamilyChange}
                    />
                ) : (
                    <FontSelectorButton
                        value="Inter"
                        disabled
                    />
                )}
            </div>

            {/* Кнопка подложки (20x20) */}
            <div className={classes.buttonWrapper}>
                {hasBackground !== undefined && onHasBackgroundChange ? (
                    <BackgroundToggleButton
                        value={hasBackground}
                        onChange={handleBackgroundToggle}
                    />
                ) : (
                    <BackgroundToggleButton
                        value={false}
                        disabled
                    />
                )}
            </div>

            {/* Кнопка цвета подложки (20x20) - показывается только при включенной подложке */}
            {hasBackground && (
                <div className={classes.buttonWrapper}>
                    {backgroundColor !== undefined &&
                    onBackgroundColorChange ? (
                        <BackgroundColorButton
                            value={backgroundColor}
                            onChange={onBackgroundColorChange}
                            customColors={customBackgroundColors}
                            onCustomColorsChange={onCustomBackgroundColorsChange}
                        />
                    ) : (
                        <BackgroundColorButton
                            value={null}
                            disabled
                        />
                    )}
                </div>
            )}
            </div>
        </TooltipProvider>
    )
}

